/**
 * assets3d/manifest.js — Asset manifest for the KickViz 3D visualizer.
 *
 * Each asset entry declares whether a real 3D model/texture file should be
 * loaded (`enabled: true`) or whether a procedural fallback should be used
 * (`enabled: false`). When enabled, the file is served from the `public/`
 * directory at the given `path`.
 *
 * To swap an asset:
 *   1. Drop the file in public/models/ (or public/textures/)
 *   2. Set enabled: true and point `path` at the public URL
 *   3. Restart dev server
 *
 * Exported helpers:
 * - isAssetEnabled(name) → boolean
 * - getAssetPath(name)   → string | null
 * - loadModel(name)      → Promise<THREE.Group | null>
 */

export const ASSET_MANIFEST = {
  pitch: {
    enabled: true,
    path: '/models/pitch/pitch_style_a.glb',
    fallback: 'procedural',
    scale: 'auto',     // auto-fitted in createFootballPitch to 105m length
    description: 'Full stadium pitch model (surface, fences, goals, floodlights)',
  },
  ball: {
    enabled: true,
    path: '/models/ball/ball_style_a.glb',
    fallback: 'procedural',
    scale: 1.0,         // auto-fitted in createBallObject to 0.28 radius
    description: 'Soccer ball model',
  },
  goals: {
    enabled: false,
    path: '/models/goals/goals.glb',
    fallback: 'procedural',
    scale: 1.0,
    description: 'Goal frame + net (instantiated at both ends)',
  },
  players: {
    enabled: true,
    path: '/models/player/player_model_a.glb',
    fallback: 'procedural',
    scale: 1.0,
    description: 'Player model (instantiated 11x per team)',
  },
  arrowhead: {
    enabled: false,
    path: '/models/arrowhead/arrowhead.glb',
    fallback: 'procedural',
    scale: 1.0,
    description: 'Arrow head for transition arrows',
  },
  skybox: {
    enabled: false,
    path: '/textures/skybox/',
    fallback: 'procedural',
    type: 'cubemap',     // 'cubemap' or 'equirectangular'
    description: 'Skybox environment map',
  },
  pitchTexture: {
    enabled: false,
    path: '/textures/pitch/grass.jpg',
    fallback: 'procedural',
    description: 'Grass texture for pitch surface',
  },
};

/**
 * Check if an asset is enabled (file should be loaded).
 * In production, this would check file existence via fetch.
 *
 * @param {string} name - Asset key in ASSET_MANIFEST.
 * @returns {boolean} True if the asset is enabled.
 */
export function isAssetEnabled(name) {
  const asset = ASSET_MANIFEST[name];
  return asset?.enabled ?? false;
}

/**
 * Get the full path for an asset.
 * Returns null if asset is disabled or not found.
 *
 * @param {string} name - Asset key in ASSET_MANIFEST.
 * @returns {string|null} The public URL path, or null.
 */
export function getAssetPath(name) {
  const asset = ASSET_MANIFEST[name];
  if (!asset?.enabled) return null;
  return asset.path;
}

/**
 * Load a GLTF/GLB model asynchronously.
 * Returns a Promise<THREE.Group> or null if disabled/failed.
 *
 * Usage in createBallObject etc:
 *   const model = await loadModel('ball');
 *   if (model) return model;
 *   // ... fall through to procedural creation
 *
 * @param {string} name - Asset key in ASSET_MANIFEST.
 * @returns {Promise<Object|null>} Cloned THREE.Group with shadows enabled, or null.
 */
export async function loadModel(name) {
  const path = getAssetPath(name);
  if (!path) return null;

  try {
    const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
    const loader = new GLTFLoader();
    const url = path;  // absolute path from public/ root
    const gltf = await loader.loadAsync(url);
    const model = gltf.scene.clone();

    // NOTE: scale/orientation is NOT applied here — each create function
    // handles its own unit conversion and axis orientation.
    model.traverse((c) => {
      if (c.isMesh) {
        c.castShadow = true;
        c.receiveShadow = true;
      }
    });
    return model;
  } catch (err) {
    console.error(`[assets3d] Failed to load ${name} from ${path}, using procedural fallback`, err);
    return null;
  }
}
